import get from 'lodash.get';
import { HTTP_METHOD, HTTP_RESPONSE_CODE } from './consts';

export class ResultsClient {
    /**
     * Constructor
     * @param {Object{}} httpClient - client that handles HTTP requests
     * @returns {undefined}
     */
    constructor(httpClient) {
        this.httpClient = httpClient;
    }

    /**
     * This is a poor-man skipped transaction handling:
     *  - if result was already saved, there will be 409 error
     *  - if result was already deleted, there will be 404 error
     * If any of those errors occur, they will not result in overall operation fail
     */
    static NON_FATAL_API_ERRORS = [HTTP_RESPONSE_CODE.CONFLICT, HTTP_RESPONSE_CODE.NOT_FOUND];

    /**
     * Get http client config for get results operation
     * @param {Object{}} params get request parameters
     * @param {Array<String>} params{}.studentUids - non-empty list of student ids to get results for
     * @param {Integer} params{}.schoolYear - school year to get results for, optional
     * @param {String} params{}.gradeLevel - grade results are associated with
     * @param {String} params{}.period - results period
     * @param {Boolean} params{}.includeInvalidated - whether invalidated results should be returned
     * @param {Array<String>} params{}.language - results' language
     * @returns {Object}
     */
    static getResultsRequestConfig({
        studentUids,
        schoolYear,
        gradeLevel,
        period,
        includeInvalidated,
        language,
    } = {}) {
        return {
            url: this.BASE_URL,
            method: HTTP_METHOD.GET,
            params: {
                studentUids: studentUids.join(','),
                schoolYear,
                gradeLevel,
                period,
                includeInvalidated,
                language: language && language.join(','),
            },
        };
    }

    /**
     * Get http client config for create result operation
     * @param {Object{}} data - result data to save
     * @returns {Object}
     */
    static getCreateResultRequestConfig(data) {
        return {
            url: this.BASE_URL,
            method: HTTP_METHOD.POST,
            data,
        };
    }

    /**
     * Get http client config for update result operation
     * @param {Object{}} data - result data to save
     * @param {String} data{}.resultId - server result id required for update
     * @returns {Object}
     */
    static getUpdateResultRequestConfig(data) {
        return {
            url: `${this.BASE_URL}/${data.resultId}`,
            method: HTTP_METHOD.PUT,
            data,
        };
    }

    /**
     * Validate if passed error is fatal by checking server response, if any.
     * If error is among allowed API response codes, swallow it.
     * Throw it otherwise.
     * @param {Object} error server response object
     * @returns {Object} passed error if it's not fatal
     */
    throwErrorIfFatal(error) {
        if (this.constructor.NON_FATAL_API_ERRORS.includes(get(error, 'response.status'))) {
            return error;
        }
        throw error;
    }

    /**
     * Send request to get result on server from provided data
     * @param {Array<String>} studentUids - non-empty list of student ids to get results for
     * @param {Integer} schoolYear - school year to get results for, optional
     * @param {Boolean} [includeInvalidated=false] - whether to include invalidated results
     * @param {Array<String>} [language=[]] - results' language, e.g. ['eng', 'spa']
     */
    async getResults(studentUids, schoolYear, includeInvalidated = false, language = []) {
        const baseUrl = this.constructor.BASE_URL;
        return this.httpClient.request(
            this.constructor.getResultsRequestConfig({
                baseUrl,
                studentUids,
                schoolYear,
                includeInvalidated,
                language,
            }),
        );
    }

    /**
     * Send request to create result on server from provided data
     * @param {Object} result - result data
     * @returns {Promise}
     */
    async createResult(result) {
        const response = await this.httpClient.post(this.constructor.BASE_URL, result);
        return response || {};
    }

    /**
     * Update server result by id with provided data
     * @param {Object} result - updated result data
     * @param {String} result{}.resultId - server result id required for update
     * @returns {Promise}
     */
    async updateResult(result) {
        const response = await this.httpClient.put(
            `${this.constructor.BASE_URL}/${result.resultId}`,
            result,
        );
        return response || {};
    }

    /**
     * Save results to server. Results are distinguished by the presence of
     * `resultId` key in result body when choosing whether it's create or update operation.
     * Update operations, if any, are performed before all create operations.
     * @param {Array<Object>} results - list of results to send to server
     * @returns {Promise} Promise which resolves when all result requests are completed
     *                    with success or non-fatal error status.
     *                    Promise resolves with the list of server responses, both successful and failed:
     *                    [ updateResponse_1, ..., updateResponse_N, createResponse_1, ..., createResponse_M]
     *                    Promise rejects with the first fatal error.
     */
    async persistResults(results) {
        const [resultForUpdate, resultsForCreate] = results.reduce(
            ([update, create], result) =>
                result.resultId ? [[...update, result], create] : [update, [...create, result]],
            [[], []],
        );
        try {
            const updateResponses = await Promise.all(
                resultForUpdate.map(result =>
                    this.updateResult(result).catch(error => this.throwErrorIfFatal(error)),
                ),
            );
            const createResponses = await Promise.all(
                resultsForCreate.map(result =>
                    this.createResult(result).catch(error => this.throwErrorIfFatal(error)),
                ),
            );
            return updateResponses.concat(createResponses);
        } catch (error) {
            return Promise.reject(error);
        }
    }
}

export default ResultsClient;
